import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Typography,
  Stepper,
  Step,
  StepLabel,
  StepContent,
  Button,
  Card,
  CardContent,
  CardActions,
  Grid,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  IconButton,
  Chip,
  LinearProgress,
  Alert,
  Paper,
  Divider,
  Tabs,
  Tab,
  Badge,
  Tooltip,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  CircularProgress,
} from '@mui/material';
import {
  PlayArrowOutlined as PlayIcon,
  PauseOutlined as PauseIcon,
  StopOutlined as StopIcon,
  SaveOutlined as SaveIcon,
  DownloadOutlined as DownloadIcon,
  DeleteOutlineOutlined as DeleteIcon,
  AddOutlined as AddIcon,
  CheckCircleOutlineOutlined as CheckIcon,
  ErrorOutlineOutlined as ErrorIcon,
  ScheduleOutlined as PendingIcon,
  FolderOutlined as ProjectIcon,
  DescriptionOutlined as TemplateIcon,
  TimelineOutlined as TimelineIcon,
  AssessmentOutlined as AssessmentIcon,
  SettingsOutlined as SettingsIcon,
  AutoStoriesOutlined as StoryIcon,
  BrushOutlined as ArtIcon,
  CodeOutlined as TechIcon,
  RestaurantOutlined as CuisineIcon,
  CampaignOutlined as MarketingIcon,
  MusicNoteOutlined as MusicIcon,
  SchoolOutlined as EducationalIcon,
  BusinessCenterOutlined as EnterpriseIcon,
  PaletteOutlined as DesignIcon,
  ScienceOutlined as ResearchIcon,
  MovieOutlined as EntertainmentIcon,
  ShoppingBagOutlined as ProductIcon,
  TrendingUpOutlined as MarketResearchIcon,
  InsightsOutlined as IntelligenceIcon,
  QueryStatsOutlined as AnalyticsIcon,
} from '@mui/icons-material';
import { WorkflowEngine } from '../services/workflowEngine';
import { PROJECT_TEMPLATES, getProjectTemplate } from '../config/projectTemplates';
import {
  StudioProject,
  ProjectPhase,
  ProjectTemplate,
  CreativeProjectConfig,
  WorkflowState,
  PhaseStatus,
} from '../models/genericStudio';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

const TabPanel: React.FC<TabPanelProps> = ({ children, value, index }) => {
  return (
    <div hidden={value !== index}>
      {value === index && <Box sx={{ py: 3 }}>{children}</Box>}
    </div>
  );
};

const getPhaseIcon = (status: PhaseStatus) => {
  switch (status) {
    case 'completed':
      return <CheckIcon color="success" />;
    case 'in-progress':
      return <CircularProgress size={20} />;
    case 'failed':
      return <ErrorIcon color="error" />;
    case 'skipped':
      return <PendingIcon color="disabled" />;
    default:
      return <PendingIcon />;
  }
};

const getTemplateIcon = (templateType: string) => {
  switch (templateType) {
    case 'story':
      return StoryIcon;
    case 'visual-art':
      return ArtIcon;
    case 'technical':
      return TechIcon;
    case 'marketing':
      return MarketingIcon;
    case 'educational':
      return EducationalIcon;
    case 'research-report':
      return ResearchIcon;
    case 'market-research':
      return MarketResearchIcon;
    case 'competitive-intelligence':
      return IntelligenceIcon;
    case 'culinary':
      return CuisineIcon;
    case 'music':
      return MusicIcon;
    case 'entertainment':
      return EntertainmentIcon;
    case 'enterprise':
      return EnterpriseIcon;
    case 'design':
      return DesignIcon;
    case 'product':
      return ProductIcon;
    default:
      return ProjectIcon;
  }
};

export const CreativeProjectStudio: React.FC = () => {
  const [activeTab, setActiveTab] = useState(0);
  const [projects, setProjects] = useState<StudioProject[]>([]);
  const [selectedProject, setSelectedProject] = useState<StudioProject | null>(null);
  const [workflowState, setWorkflowState] = useState<WorkflowState | null>(null);
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState<ProjectTemplate | null>(null);
  const [projectName, setProjectName] = useState('');
  const [projectDescription, setProjectDescription] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [userInputsDialogOpen, setUserInputsDialogOpen] = useState(false);
  const [userInputs, setUserInputs] = useState<Record<string, any>>({});

  useEffect(() => {
    loadProjects();
  }, []);

  useEffect(() => {
    if (selectedProject) {
      const state = WorkflowEngine.getWorkflowState(selectedProject.id);
      setWorkflowState(state);
    }
  }, [selectedProject]);

  const loadProjects = () => {
    const allProjects = WorkflowEngine.getAllProjects();
    setProjects(allProjects.filter(p => p.type === 'creative'));
  };

  const handleCreateProject = () => {
    if (!selectedTemplate || !projectName) return;

    const newProject: StudioProject = {
      id: `creative-${Date.now()}`,
      type: 'creative',
      projectType: selectedTemplate.type,
      name: projectName,
      description: projectDescription,
      phases: selectedTemplate.phases.map(p => ({ ...p })), // Deep copy
      currentPhase: 0,
      agentSessions: new Map(),
      results: {
        artifacts: [],
        insights: [],
        recommendations: [],
        exportFormats: [],
      },
      metadata: {
        template: selectedTemplate.id,
        tags: selectedTemplate.tags,
        version: 1,
      },
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
      status: 'draft',
    };

    WorkflowEngine.saveProject(newProject);
    loadProjects();
    setSelectedProject(newProject);
    setCreateDialogOpen(false);
    setProjectName('');
    setProjectDescription('');
    setSelectedTemplate(null);
  };

  const handleStartWorkflow = async () => {
    if (!selectedProject) return;

    // Show user inputs dialog for story and visual art projects
    if ((selectedProject.projectType === 'story' && !selectedProject.metadata.userInputs?.concept) ||
        (selectedProject.projectType === 'visual-art' && !selectedProject.metadata.userInputs?.theme)) {
      setUserInputsDialogOpen(true);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      // Save user inputs if any
      if (Object.keys(userInputs).length > 0) {
        selectedProject.metadata.userInputs = userInputs;
        WorkflowEngine.saveProject(selectedProject);
      }

      selectedProject.status = 'active';
      WorkflowEngine.saveProject(selectedProject);

      // Execute phases sequentially
      for (let i = 0; i < selectedProject.phases.length; i++) {
        if (workflowState?.paused) break;

        await WorkflowEngine.executePhase(
          selectedProject,
          i,
          (progress) => {
            // Update UI with progress
            const state = WorkflowEngine.getWorkflowState(selectedProject.id);
            setWorkflowState(state);
          }
        );

        // Reload project to get updated phase results
        const updatedProject = WorkflowEngine.getProject(selectedProject.id);
        if (updatedProject) {
          setSelectedProject(updatedProject);
        }
      }

      // Mark project as completed
      if (selectedProject) {
        selectedProject.status = 'completed';
        WorkflowEngine.saveProject(selectedProject);
      }
    } catch (err: any) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleConfirmUserInputs = () => {
    if (!selectedProject) return;
    
    selectedProject.metadata.userInputs = userInputs;
    WorkflowEngine.saveProject(selectedProject);
    setUserInputsDialogOpen(false);
    handleStartWorkflow();
  };

  const handlePauseWorkflow = () => {
    if (!selectedProject) return;
    WorkflowEngine.pauseWorkflow(selectedProject.id);
    const state = WorkflowEngine.getWorkflowState(selectedProject.id);
    setWorkflowState(state);
  };

  const handleResumeWorkflow = () => {
    if (!selectedProject) return;
    WorkflowEngine.resumeWorkflow(selectedProject.id);
    handleStartWorkflow();
  };

  const handleDeleteProject = (projectId: string) => {
    WorkflowEngine.deleteProject(projectId);
    if (selectedProject?.id === projectId) {
      setSelectedProject(null);
    }
    loadProjects();
  };

  const handleExportProject = () => {
    if (!selectedProject) return;

    const exportData = {
      project: selectedProject,
      workflowState,
      exportedAt: new Date().toISOString(),
    };

    const blob = new Blob([JSON.stringify(exportData, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${selectedProject.name.replace(/\s+/g, '-')}-${Date.now()}.json`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const renderProjectCard = (project: StudioProject) => {
    const template = getProjectTemplate(project.metadata.template || '');
    const completedPhases = project.phases.filter(p => p.status === 'completed').length;
    const progress = (completedPhases / project.phases.length) * 100;

    return (
      <Card key={project.id} sx={{ height: '100%' }}>
        <CardContent>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
            <Typography variant="h6" sx={{ flex: 1 }}>
              {project.name}
            </Typography>
            <Chip
              label={project.status}
              size="small"
              color={project.status === 'completed' ? 'success' : 'default'}
            />
          </Box>
          
          <Typography variant="body2" color="text.secondary" paragraph>
            {project.description || template?.description}
          </Typography>

          <Box sx={{ mb: 2 }}>
            <Typography variant="caption" color="text.secondary">
              Progress: {completedPhases}/{project.phases.length} phases
            </Typography>
            <LinearProgress variant="determinate" value={progress} sx={{ mt: 0.5 }} />
          </Box>

          <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
            {project.metadata.tags.slice(0, 3).map((tag, i) => (
              <Chip key={i} label={tag} size="small" variant="outlined" />
            ))}
          </Box>
        </CardContent>
        <CardActions>
          <Button
            size="small"
            onClick={() => {
              setSelectedProject(project);
              setActiveTab(1); // Switch to Workflow tab
            }}
            startIcon={<ProjectIcon />}
          >
            Open
          </Button>
          <IconButton
            size="small"
            onClick={() => handleDeleteProject(project.id)}
            color="error"
          >
            <DeleteIcon />
          </IconButton>
        </CardActions>
      </Card>
    );
  };

  const renderPhaseDetails = (phase: ProjectPhase, index: number) => {
    return (
      <Step key={phase.id}>
        <StepLabel
          icon={getPhaseIcon(phase.status)}
          optional={
            phase.completedAt && (
              <Typography variant="caption">
                Completed: {new Date(phase.completedAt).toLocaleString()}
              </Typography>
            )
          }
        >
          {phase.name}
        </StepLabel>
        <StepContent>
          <Typography variant="body2" paragraph>
            {phase.description}
          </Typography>
          
          <Typography variant="subtitle2" gutterBottom>
            Agents ({phase.agents.length}):
          </Typography>
          <List dense>
            {phase.agents.map((agent, i) => (
              <ListItem key={i}>
                <ListItemText
                  primary={agent.agentName}
                  secondary={agent.required ? 'Required' : 'Optional'}
                />
                {agent.parallel && <Chip label="Parallel" size="small" />}
              </ListItem>
            ))}
          </List>

          {phase.results && (
            <Alert severity="success" sx={{ mt: 2 }}>
              {phase.results.summary}
            </Alert>
          )}

          {phase.error && (
            <Alert severity="error" sx={{ mt: 2 }}>
              {phase.error}
            </Alert>
          )}
        </StepContent>
      </Step>
    );
  };

  return (
    <Container maxWidth="md" sx={{ py: 4 }}>
      {/* Header */}
      <Box sx={{ textAlign: 'center', mb: 4 }}>
        <Typography variant="h4" sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1, mb: 2 }}>
          <DesignIcon sx={{ fontSize: 40 }} />
          Creative Project Studio
        </Typography>
        <Typography variant="body1" color="text.secondary">
          Multi-phase creative projects with automated workflow orchestration and professional deliverables
        </Typography>
      </Box>

      <Paper 
        elevation={0}
        sx={{ 
          border: 1,
          borderColor: 'divider',
          borderRadius: 2,
          overflow: 'hidden'
        }}
      >
        <Box sx={{ p: 3 }}>
          {error && (
            <Alert 
              severity="error" 
              sx={{ mb: 2, borderRadius: 1.5 }} 
              onClose={() => setError(null)}
            >
              {error}
            </Alert>
          )}

          <Tabs 
            value={activeTab} 
            onChange={(_, val) => setActiveTab(val)}
            sx={{ mb: 2 }}
          >
            <Tab label="Projects" />
            <Tab label="Workflow" disabled={!selectedProject} />
            <Tab label="Results" disabled={!selectedProject} />
            <Tab label="Settings" disabled={!selectedProject} />
          </Tabs>

        <TabPanel value={activeTab} index={0}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 4, maxWidth: 600, mx: 'auto' }}>
            <Box sx={{ textAlign: 'center' }}>
              <Button
                variant="contained"
                size="large"
                startIcon={<AddIcon />}
                onClick={() => setCreateDialogOpen(true)}
                sx={{
                  py: 1.5,
                  borderRadius: 2,
                  fontWeight: 600,
                  fontSize: '1rem',
                  textTransform: 'none',
                }}
              >
                New Creative Project
              </Button>
            </Box>
          </Box>

          <Grid container spacing={3} sx={{ mt: 2 }}>
            {projects.map(project => (
              <Grid item xs={12} sm={6} md={4} key={project.id}>
                {renderProjectCard(project)}
              </Grid>
            ))}
            {projects.length === 0 && (
              <Grid item xs={12}>
                <Paper sx={{ p: 4, textAlign: 'center' }}>
                  <Typography variant="h6" color="text.secondary" gutterBottom>
                    No projects yet
                  </Typography>
                  <Typography variant="body2" color="text.secondary" paragraph>
                    Create your first creative project to get started
                  </Typography>
                  <Button
                    variant="outlined"
                    startIcon={<AddIcon />}
                    onClick={() => setCreateDialogOpen(true)}
                  >
                    Create Project
                  </Button>
                </Paper>
              </Grid>
            )}
          </Grid>
        </TabPanel>

        <TabPanel value={activeTab} index={1}>
          {selectedProject && (
            <Grid container spacing={3}>
              <Grid item xs={12} md={8}>
                <Paper sx={{ p: 3 }}>
                  <Typography variant="h6" gutterBottom>
                    Workflow Progress
                  </Typography>
                  
                  <Stepper activeStep={selectedProject.currentPhase} orientation="vertical">
                    {selectedProject.phases.map((phase, index) => renderPhaseDetails(phase, index))}
                  </Stepper>

                  <Box sx={{ mt: 3, display: 'flex', gap: 2 }}>
                    {selectedProject.status === 'draft' && (
                      <Button
                        variant="contained"
                        startIcon={loading ? <CircularProgress size={20} /> : <PlayIcon />}
                        onClick={handleStartWorkflow}
                        disabled={loading}
                      >
                        Start Workflow
                      </Button>
                    )}
                    
                    {selectedProject.status === 'active' && !workflowState?.paused && (
                      <Button
                        variant="outlined"
                        startIcon={<PauseIcon />}
                        onClick={handlePauseWorkflow}
                      >
                        Pause
                      </Button>
                    )}
                    
                    {selectedProject.status === 'active' && workflowState?.paused && (
                      <Button
                        variant="contained"
                        startIcon={<PlayIcon />}
                        onClick={handleResumeWorkflow}
                      >
                        Resume
                      </Button>
                    )}

                    <Button
                      variant="outlined"
                      startIcon={<DownloadIcon />}
                      onClick={handleExportProject}
                    >
                      Export
                    </Button>
                  </Box>
                </Paper>
              </Grid>

              <Grid item xs={12} md={4}>
                <Paper sx={{ p: 3 }}>
                  <Typography variant="h6" gutterBottom>
                    Project Details
                  </Typography>
                  
                  <List>
                    <ListItem>
                      <ListItemText
                        primary="Created"
                        secondary={new Date(selectedProject.createdAt).toLocaleString()}
                      />
                    </ListItem>
                    <ListItem>
                      <ListItemText
                        primary="Last Updated"
                        secondary={new Date(selectedProject.updatedAt).toLocaleString()}
                      />
                    </ListItem>
                    <ListItem>
                      <ListItemText
                        primary="Template"
                        secondary={getProjectTemplate(selectedProject.metadata.template || '')?.name}
                      />
                    </ListItem>
                    <ListItem>
                      <ListItemText
                        primary="Status"
                        secondary={selectedProject.status}
                      />
                    </ListItem>
                  </List>

                  {workflowState && (
                    <>
                      <Divider sx={{ my: 2 }} />
                      <Typography variant="subtitle2" gutterBottom>
                        Workflow Status
                      </Typography>
                      <LinearProgress
                        variant="determinate"
                        value={workflowState.progress}
                        sx={{ mb: 1 }}
                      />
                      <Typography variant="body2" color="text.secondary">
                        {workflowState.progress}% complete
                      </Typography>
                    </>
                  )}
                </Paper>
              </Grid>
            </Grid>
          )}
        </TabPanel>

        <TabPanel value={activeTab} index={2}>
          {selectedProject && (
            <Paper sx={{ p: 3 }}>
              <Typography variant="h6" gutterBottom>
                Project Results
              </Typography>
              
              {selectedProject.results.artifacts.length > 0 ? (
                <>
                  <Grid container spacing={2} sx={{ mb: 3 }}>
                    {selectedProject.results.artifacts.map((artifact, index) => (
                      <Grid item xs={12} sm={6} md={4} key={artifact.id}>
                        <Card>
                          <CardContent>
                            <Typography variant="subtitle1" gutterBottom>{artifact.name}</Typography>
                            <Typography variant="body2" color="text.secondary">
                              Type: {artifact.type}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              Created by: {artifact.createdBy}
                            </Typography>
                            {artifact.type === 'image' && artifact.url && (
                              <Box sx={{ mt: 1 }}>
                                <img 
                                  src={artifact.url} 
                                  alt={artifact.name} 
                                  style={{ width: '100%', height: 'auto', borderRadius: 4 }}
                                />
                              </Box>
                            )}
                            {artifact.type === 'text' && artifact.content && (
                              <Box sx={{ mt: 1, maxHeight: 200, overflow: 'auto' }}>
                                <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                                  {artifact.content}
                                </Typography>
                              </Box>
                            )}
                          </CardContent>
                          <CardActions>
                            {artifact.url && (
                              <Button size="small" href={artifact.url} target="_blank">
                                View
                              </Button>
                            )}
                            {artifact.content && (
                              <Button 
                                size="small" 
                                onClick={() => navigator.clipboard.writeText(artifact.content)}
                              >
                                Copy
                              </Button>
                            )}
                          </CardActions>
                        </Card>
                      </Grid>
                    ))}
                  </Grid>

                  {/* Phase Results Summary */}
                  {selectedProject.phases.filter(p => p.results).map((phase, index) => (
                    <Box key={phase.id} sx={{ mb: 3 }}>
                      <Typography variant="h6" gutterBottom>
                        {phase.name} Results
                      </Typography>
                      <Paper sx={{ p: 2 }}>
                        <Typography variant="body2" paragraph>
                          {phase.results?.summary}
                        </Typography>
                        {phase.results?.agentResults && (
                          <List dense>
                            {Array.from(phase.results.agentResults.entries()).map(([agent, result]) => (
                              <ListItem key={agent}>
                                <ListItemText
                                  primary={result.agentName}
                                  secondary={result.success ? 'Completed successfully' : 'Failed'}
                                />
                              </ListItem>
                            ))}
                          </List>
                        )}
                      </Paper>
                    </Box>
                  ))}
                </>
              ) : (
                <Typography variant="body2" color="text.secondary">
                  No results yet. Run the workflow to generate results.
                </Typography>
              )}
            </Paper>
          )}
        </TabPanel>

        <TabPanel value={activeTab} index={3}>
          {selectedProject && (
            <Paper sx={{ p: 3 }}>
              <Typography variant="h6" gutterBottom>
                Project Settings
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Settings and configuration options will be available here.
              </Typography>
            </Paper>
          )}
        </TabPanel>

        {/* Create Project Dialog */}
        <Dialog 
          open={createDialogOpen} 
          onClose={() => setCreateDialogOpen(false)} 
          maxWidth="md" 
          fullWidth
          PaperProps={{
            sx: {
              borderRadius: 2,
              border: 1,
              borderColor: 'divider',
            }
          }}
        >
          <DialogTitle sx={{ textAlign: 'center', pb: 1 }}>
            <Typography variant="h5" sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1 }}>
              <AddIcon />
              Create New Creative Project
            </Typography>
          </DialogTitle>
          <DialogContent>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, mt: 2 }}>
              <TextField
                fullWidth
                label="Project Name"
                placeholder="e.g., Brand Identity Design Project"
                value={projectName}
                onChange={(e) => setProjectName(e.target.value)}
                required
              />
              
              <TextField
                fullWidth
                multiline
                rows={3}
                label="Project Description"
                placeholder="Brief description of your creative project goals..."
                value={projectDescription}
                onChange={(e) => setProjectDescription(e.target.value)}
              />

              <Box>
                <Typography variant="h6" gutterBottom>
                  Choose a Template
                </Typography>
            
            <Grid container spacing={2}>
              {PROJECT_TEMPLATES.map(template => {
                const IconComponent = getTemplateIcon(template.type);
                return (
                  <Grid item xs={12} sm={6} key={template.id}>
                    <Card
                      sx={{
                        cursor: 'pointer',
                        border: selectedTemplate?.id === template.id ? 2 : 1,
                        borderColor: selectedTemplate?.id === template.id ? 'primary.main' : 'divider',
                        transition: 'all 0.2s ease',
                        '&:hover': {
                          borderColor: 'primary.main',
                          transform: 'translateY(-2px)',
                          boxShadow: 2,
                        },
                      }}
                      onClick={() => setSelectedTemplate(template)}
                    >
                      <CardContent>
                        <Box sx={{ display: 'flex', alignItems: 'flex-start', mb: 2 }}>
                          <Box
                            sx={{
                              width: 48,
                              height: 48,
                              borderRadius: 2,
                              border: 1,
                              borderColor: 'divider',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              mr: 2,
                              backgroundColor: selectedTemplate?.id === template.id 
                                ? 'action.selected' 
                                : 'background.default',
                            }}
                          >
                            <IconComponent sx={{ fontSize: 28, color: 'text.primary' }} />
                          </Box>
                          <Box sx={{ flex: 1 }}>
                            <Typography variant="h6" sx={{ fontWeight: 600, mb: 0.5 }}>
                              {template.name}
                            </Typography>
                            <Chip 
                              label={template.difficulty} 
                              size="small" 
                              variant="outlined"
                              sx={{ 
                                borderRadius: 1,
                                fontSize: '0.7rem',
                                height: 20,
                              }}
                            />
                          </Box>
                        </Box>
                        <Typography variant="body2" color="text.secondary" paragraph>
                          {template.description}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {template.estimatedTime} • {template.phases.length} phases
                        </Typography>
                      </CardContent>
                    </Card>
                  </Grid>
                );
              })}
            </Grid>
              </Box>
            </Box>
          </DialogContent>
          <DialogActions sx={{ p: 3, pt: 1 }}>
            <Button 
              onClick={() => setCreateDialogOpen(false)}
              sx={{ borderRadius: 1.5 }}
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              onClick={handleCreateProject}
              disabled={!projectName || !selectedTemplate}
              sx={{
                borderRadius: 1.5,
                px: 3,
                fontWeight: 600,
                textTransform: 'none',
              }}
            >
              Create Project
            </Button>
          </DialogActions>
        </Dialog>

        {/* User Inputs Dialog */}
        <Dialog 
          open={userInputsDialogOpen} 
          onClose={() => setUserInputsDialogOpen(false)} 
          maxWidth="sm" 
          fullWidth
          PaperProps={{
            sx: {
              borderRadius: 2,
              border: 1,
              borderColor: 'divider',
            }
          }}
        >
          <DialogTitle sx={{ textAlign: 'center', pb: 1 }}>
            <Typography variant="h5" sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1 }}>
              <SettingsIcon />
              Project Configuration
            </Typography>
          </DialogTitle>
          <DialogContent>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, mt: 2 }}>
              <Typography variant="body2" color="text.secondary">
              Provide initial inputs to guide the creative process
            </Typography>
            
            {selectedProject?.projectType === 'story' && (
              <>
                <TextField
                  fullWidth
                  label="Story Concept"
                  placeholder="What's your story about?"
                  value={userInputs.concept || ''}
                  onChange={(e) => setUserInputs({ ...userInputs, concept: e.target.value })}
                  sx={{ mb: 2, mt: 1 }}
                  multiline
                  rows={2}
                />
                
                <TextField
                  fullWidth
                  label="Main Character Name (optional)"
                  value={userInputs.characterName || ''}
                  onChange={(e) => setUserInputs({ ...userInputs, characterName: e.target.value })}
                  sx={{ mb: 2 }}
                />
                
                <FormControl fullWidth sx={{ mb: 2 }}>
                  <InputLabel>Genre</InputLabel>
                  <Select
                    value={userInputs.genre || 'general'}
                    onChange={(e) => setUserInputs({ ...userInputs, genre: e.target.value })}
                    label="Genre"
                  >
                    <MenuItem value="general">General</MenuItem>
                    <MenuItem value="fantasy">Fantasy</MenuItem>
                    <MenuItem value="sci-fi">Science Fiction</MenuItem>
                    <MenuItem value="mystery">Mystery</MenuItem>
                    <MenuItem value="romance">Romance</MenuItem>
                    <MenuItem value="thriller">Thriller</MenuItem>
                  </Select>
                </FormControl>

                <FormControl fullWidth sx={{ mb: 2 }}>
                  <InputLabel>Tone</InputLabel>
                  <Select
                    value={userInputs.tone || 'balanced'}
                    onChange={(e) => setUserInputs({ ...userInputs, tone: e.target.value })}
                    label="Tone"
                  >
                    <MenuItem value="balanced">Balanced</MenuItem>
                    <MenuItem value="serious">Serious</MenuItem>
                    <MenuItem value="humorous">Humorous</MenuItem>
                    <MenuItem value="dark">Dark</MenuItem>
                    <MenuItem value="uplifting">Uplifting</MenuItem>
                  </Select>
                </FormControl>
              </>
            )}

            {selectedProject?.projectType === 'visual-art' && (
              <>
                <TextField
                  fullWidth
                  label="Art Theme"
                  placeholder="What theme or concept for your art collection?"
                  value={userInputs.theme || ''}
                  onChange={(e) => setUserInputs({ ...userInputs, theme: e.target.value })}
                  sx={{ mb: 2, mt: 1 }}
                  multiline
                  rows={2}
                />
                
                <FormControl fullWidth sx={{ mb: 2 }}>
                  <InputLabel>Art Style</InputLabel>
                  <Select
                    value={userInputs.style || 'contemporary'}
                    onChange={(e) => setUserInputs({ ...userInputs, style: e.target.value })}
                    label="Art Style"
                  >
                    <MenuItem value="contemporary">Contemporary</MenuItem>
                    <MenuItem value="realistic">Realistic</MenuItem>
                    <MenuItem value="abstract">Abstract</MenuItem>
                    <MenuItem value="impressionist">Impressionist</MenuItem>
                    <MenuItem value="surreal">Surreal</MenuItem>
                    <MenuItem value="digital">Digital Art</MenuItem>
                  </Select>
                </FormControl>
              </>
            )}

            {selectedProject?.projectType === 'technical' && (
              <>
                <TextField
                  fullWidth
                  label="Application Name"
                  value={userInputs.appName || ''}
                  onChange={(e) => setUserInputs({ ...userInputs, appName: e.target.value })}
                  sx={{ mb: 2, mt: 1 }}
                />
                
                <TextField
                  fullWidth
                  label="Application Description"
                  placeholder="What will your application do?"
                  value={userInputs.appDescription || ''}
                  onChange={(e) => setUserInputs({ ...userInputs, appDescription: e.target.value })}
                  sx={{ mb: 2 }}
                  multiline
                  rows={2}
                />

                <FormControl fullWidth sx={{ mb: 2 }}>
                  <InputLabel>Application Type</InputLabel>
                  <Select
                    value={userInputs.appType || 'web-app'}
                    onChange={(e) => setUserInputs({ ...userInputs, appType: e.target.value })}
                    label="Application Type"
                  >
                    <MenuItem value="web-app">Web Application</MenuItem>
                    <MenuItem value="mobile-app">Mobile App</MenuItem>
                    <MenuItem value="desktop-app">Desktop Application</MenuItem>
                    <MenuItem value="api">API Service</MenuItem>
                    <MenuItem value="full-stack">Full Stack Platform</MenuItem>
                  </Select>
                </FormControl>
              </>
            )}
            </Box>
          </DialogContent>
          <DialogActions sx={{ p: 3, pt: 1 }}>
            <Button 
              onClick={() => setUserInputsDialogOpen(false)}
              sx={{ borderRadius: 1.5 }}
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              onClick={handleConfirmUserInputs}
              disabled={
                (selectedProject?.projectType === 'story' && !userInputs.concept) ||
                (selectedProject?.projectType === 'visual-art' && !userInputs.theme) ||
                (selectedProject?.projectType === 'technical' && !userInputs.appDescription)
              }
              sx={{
                borderRadius: 1.5,
                px: 3,
                fontWeight: 600,
                textTransform: 'none',
              }}
            >
              Start Workflow
            </Button>
          </DialogActions>
        </Dialog>
        </Box>
      </Paper>
    </Container>
  );
};